import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, Send, ChevronDown, ChevronUp, Book, FileText, Home, Heart, Mic } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function FAQPage() {
  const [faqs, setFaqs] = useState([]);
  const [articles, setArticles] = useState([]);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [newQuestion, setNewQuestion] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('faqs');

  const fetchContent = useCallback(async () => {
    try {
      const [faqsRes, articlesRes] = await Promise.all([
        axios.get(`${API}/faqs`),
        axios.get(`${API}/articles`)
      ]);
      setFaqs(faqsRes.data);
      setArticles(articlesRes.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error fetching content:', error);
    }
  }, []);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  const submitQuestion = async (e) => {
    e.preventDefault();
    if (!newQuestion.trim()) return;
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/faqs`, { question: newQuestion });
      toast.success('Question submitted! Check back for an answer.');
      setNewQuestion('');
      fetchContent();
    } catch (error) {
      toast.error('Failed to submit question');
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen py-24 md:py-32" data-testid="faq-page">
      <div className="container-custom">
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="w-20 h-20 rounded-full bg-secondary/20 flex items-center justify-center mx-auto mb-6">
            <Mic className="w-10 h-10 text-secondary" />
          </div>
          <h1 className="section-header mb-4">Low FAQs & Raps</h1>
          <p className="section-subheader max-w-2xl mx-auto">
            Got questions? Get answers. Plus updates, articles, and behind-the-scenes content.
          </p>
        </motion.div>

        {/* Quick Links */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex flex-wrap justify-center gap-4 mb-12"
        >
          <Link to="/publications">
            <Button variant="outline" className="gap-2">
              <FileText className="w-4 h-4" />
              Publications
            </Button>
          </Link>
          <a href="https://www.amazon.com/vdp/0ce1e9c32b2a44af8f885a7928faf536?product=B0FW7QRFJQ" target="_blank" rel="noopener noreferrer">
            <Button variant="outline" className="gap-2">
              <Book className="w-4 h-4" />
              Get the Book
            </Button>
          </a>
        </motion.div>

        {/* Tabs */}
        <div className="flex justify-center gap-4 mb-8">
          <button
            onClick={() => setActiveTab('faqs')}
            className={`px-6 py-3 rounded-full font-semibold transition-all ${
              activeTab === 'faqs' 
                ? 'bg-secondary text-white' 
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            <MessageCircle className="w-4 h-4 inline mr-2" />
            FAQs
          </button>
          <button
            onClick={() => setActiveTab('updates')}
            className={`px-6 py-3 rounded-full font-semibold transition-all ${
              activeTab === 'updates' 
                ? 'bg-secondary text-white' 
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            <FileText className="w-4 h-4 inline mr-2" />
            Updates & Articles
          </button>
        </div>

        <AnimatePresence mode="wait">
          {activeTab === 'faqs' && (
            <motion.div
              key="faqs"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              {/* Ask a Question */}
              <Card className="bg-card border-accent/30 max-w-2xl mx-auto mb-12">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                    <MessageCircle className="w-5 h-5 text-accent" />
                    Ask a Question
                  </h3>
                  <form onSubmit={submitQuestion} className="flex gap-3">
                    <input
                      type="text"
                      value={newQuestion}
                      onChange={(e) => setNewQuestion(e.target.value)}
                      placeholder="What would you like to know?"
                      className="flex-1 input-dark rounded-lg"
                    />
                    <Button type="submit" className="btn-primary" disabled={submitting}>
                      <Send className="w-4 h-4" />
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* FAQ List */}
              <div className="max-w-3xl mx-auto space-y-4">
                {faqs.length === 0 ? (
                  <Card className="bg-card border-border/50">
                    <CardContent className="p-8 text-center">
                      <p className="text-muted-foreground">No FAQs yet. Be the first to ask a question!</p>
                    </CardContent>
                  </Card>
                ) : (
                  faqs.map((faq, index) => (
                    <motion.div
                      key={faq.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <Card 
                        className={`bg-card border-border/50 cursor-pointer transition-all ${
                          expandedFaq === faq.id ? 'border-accent/50' : ''
                        }`}
                        onClick={() => setExpandedFaq(expandedFaq === faq.id ? null : faq.id)}
                      >
                        <CardContent className="p-5">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <h4 className="font-bold text-lg">{faq.question}</h4>
                              <AnimatePresence>
                                {expandedFaq === faq.id && faq.answer && (
                                  <motion.p
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    className="text-muted-foreground mt-3 pt-3 border-t border-border/50"
                                  >
                                    {faq.answer}
                                  </motion.p>
                                )}
                                {expandedFaq === faq.id && !faq.answer && (
                                  <motion.p
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    className="text-accent/70 mt-3 pt-3 border-t border-border/50 italic"
                                  >
                                    Answer coming soon...
                                  </motion.p>
                                )}
                              </AnimatePresence>
                            </div>
                            {expandedFaq === faq.id ? (
                              <ChevronUp className="w-5 h-5 text-accent flex-shrink-0" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))
                )}
              </div>
            </motion.div>
          )}

          {activeTab === 'updates' && (
            <motion.div
              key="updates"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="max-w-3xl mx-auto"
            >
              {articles.length === 0 ? (
                <Card className="bg-card border-border/50">
                  <CardContent className="p-8 text-center">
                    <p className="text-muted-foreground">No articles yet. Check back soon for updates!</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-6">
                  {articles.map((article, index) => (
                    <motion.div
                      key={article.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <Card className="bg-card border-border/50">
                        <CardContent className="p-6">
                          <span className="text-xs uppercase tracking-wider text-secondary font-bold">
                            {article.category}
                          </span>
                          <h3 className="text-xl font-bold mt-2 mb-3" style={{ fontFamily: 'Playfair Display' }}>
                            {article.title}
                          </h3>
                          <p className="text-muted-foreground whitespace-pre-wrap">{article.content}</p>
                          <p className="text-xs text-muted-foreground mt-4">
                            {new Date(article.created_at).toLocaleDateString()}
                          </p>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Navigation Links */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex flex-wrap justify-center gap-4 mt-16"
        >
          <Link to="/">
            <Button variant="outline" className="gap-2">
              <Home className="w-4 h-4" />
              Back to Home
            </Button>
          </Link>
          <Link to="/donate">
            <Button className="btn-primary gap-2">
              <Heart className="w-4 h-4" />
              Support / Donate
            </Button>
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
